import { Alert, Button } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { Fmt } from "../../../../../formatters";
import { usePrimaryOrganization } from "../../../../../hooks";
import { useClosedMessageTemplate } from "../../../../../hooks/useTemplates";
import { ATTACHMENT_FILE_INPUT_ACCEPT } from "../../../../../constants/attachmentConstraints";
import { Callout, CalloutVariant } from "../../../../Callout";
import { AttachmentList } from "../../../../organisms/AttachmentList";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";

export const SendProfilingData: React.FC = () => {
  const [org] = usePrimaryOrganization();
  const { request, attachment, setStepCompleted } = useDataRequestProcessingContext();

  const [template] = useClosedMessageTemplate(org.id, request.id);
  const fullName = Fmt.DataSubject.fmtFullName(request);

  const uploadEl = useRef<HTMLInputElement>(null);
  const removeFile = (index: number) => {
    attachment?.removeFile(index);
  };

  const names = useMemo(() => attachment?.files?.map((f) => f.name) ?? [], [attachment]);

  const instructions = (
    <>
      Upload the document you compiled in the previous step and click "Send" below. If you have no
      data to send, click "Send" to send the message. After the message is sent, close the request.
    </>
  );

  const fileUploadNeeded = useMemo(() => {
    return (
      request.consumerGroupResult !== "NOT_FOUND" &&
      request.consumerGroupResult !== "PROCESSED_AS_SERVICE_PROVIDER"
    );
  }, [request.consumerGroupResult]);

  useEffect(() => {
    setStepCompleted(true);
  }, [setStepCompleted]);

  return (
    <>
      <div className="text-t3 mb-std">{instructions}</div>

      {request.requestorContacted && (
        <div className="w-640">
          <Callout variant={CalloutVariant.Green} className="mb-std">
            Done! Message successfully sent to {fullName}. You can now close this request.
          </Callout>
        </div>
      )}

      {!request.requestorContacted && (
        <>
          {fileUploadNeeded && (
            <div className="mb-lg">
              {attachment.fileError && (
                <div className="mb-sm">
                  <Alert severity="error">{attachment.fileError}</Alert>
                </div>
              )}

              <AttachmentList className="mb-md" onRemove={removeFile} attachmentNames={names} />

              <label htmlFor="note-upload" className="mr-md">
                <input
                  style={{ display: "none" }}
                  ref={uploadEl}
                  id="note-upload"
                  type="file"
                  accept={ATTACHMENT_FILE_INPUT_ACCEPT}
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      attachment?.addFiles(Array.from(e.target.files));
                    }
                  }}
                />
                <Button
                  variant="contained"
                  className="btn-md"
                  onClick={() => uploadEl.current?.click()}
                >
                  Upload
                </Button>
              </label>
            </div>
          )}

          <Callout variant={CalloutVariant.Gray} className="mb-md">
            <div className="text-t3">
              <p>
                <strong>To: {request.subjectEmailAddress}</strong>
              </p>
              <p>
                <strong>Subject: A message about your request {request.id}</strong>
              </p>
              <div dangerouslySetInnerHTML={{ __html: template }} />
            </div>
          </Callout>
        </>
      )}
    </>
  );
};
